import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  LinearProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";
import useApi from "../../hooks/useApi";
import * as pdfjsLib from "pdfjs-dist/webpack";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const DocumentModal = ({ open, onClose, scope, id }) => {
  const { uploadFile, getData, postData } = useApi();
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [folderPath, setFolderPath] = useState("");
  const [folderMode, setFolderMode] = useState("new");
  const [selectedExistingFolder, setSelectedExistingFolder] = useState("");
  const [existingFolders, setExistingFolders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [results, setResults] = useState([]);

  const handleFileChange = (event) => {
    setFiles(Array.from(event.target.files));
  };

  const normalizeFolderPath = (value) => {
    return String(value || "")
      .trim()
      .replace(/\\/g, "/")
      .replace(/\/+/g, "/")
      .replace(/^\/+|\/+$/g, "");
  };

  const resolveStoredKey = (uploadResult, fallbackKey) => {
    const keyFromResult = String(uploadResult?.key || "").trim();
    if (keyFromResult.includes("/")) {
      return keyFromResult;
    }

    const fullKeyFromResult = String(uploadResult?.fullKey || "").trim();
    if (fullKeyFromResult) {
      return fullKeyFromResult;
    }

    if (keyFromResult) {
      return `documents/${keyFromResult}`;
    }

    return fallbackKey;
  };

  const enforceDocumentMetadata = async ({
    uploadResult,
    fallbackKey,
    name,
    description,
    folderPath,
  }) => {
    const storedKey = resolveStoredKey(uploadResult, fallbackKey);

    if (!storedKey) return;

    try {
      await postData({
        entityName: "Document",
        action: "updateMetadata",
        data: {
          metadata: {
            key: storedKey,
            name,
            description,
            folderPath,
            category: folderPath,
          },
        },
      });
    } catch (err) {
      console.warn(
        "Failed to enforce document metadata after upload:",
        err?.message,
      );
    }
  };

  useEffect(() => {
    const loadExistingFolders = async () => {
      if (!open) return;

      setLoadingFolders(true);
      try {
        const filter = { scope, folder: "documents" };
        if (scope === "client") filter.clientId = id;
        if (scope === "project") filter.projectId = id;

        const docs = await getData({
          entityName: "Document",
          filter,
        });

        if (!docs?.isSuccessful || !Array.isArray(docs?.data)) {
          setExistingFolders([]);
          return;
        }

        const folderSet = new Set();
        for (const doc of docs.data) {
          const normalized = normalizeFolderPath(
            doc?.folderPath || doc?.category || "",
          );
          if (normalized) folderSet.add(normalized);
        }

        const folderList = Array.from(folderSet).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        );

        setExistingFolders(folderList);

        if (folderList.length === 0) {
          setFolderMode("new");
          setSelectedExistingFolder("");
        } else if (!selectedExistingFolder) {
          setSelectedExistingFolder(folderList[0]);
        }
      } catch (err) {
        console.error("Failed to load existing document folders:", err);
        setExistingFolders([]);
      } finally {
        setLoadingFolders(false);
      }
    };

    loadExistingFolders();
  }, [open, scope, id]);

  const effectiveFolderPath = useMemo(() => {
    if (folderMode === "existing") {
      return normalizeFolderPath(selectedExistingFolder);
    }

    return normalizeFolderPath(folderPath);
  }, [folderMode, selectedExistingFolder, folderPath]);

  const handleUpload = async () => {
    setUploading(true);
    const uploaded = [];
    const normalizedFolderPath = effectiveFolderPath;

    for (const file of files) {
      const extension = file.name.includes(".")
        ? file.name.substring(file.name.lastIndexOf("."))
        : "";
      const key = `${uuidv4()}`;

      try {
        const metadata = {
          key,
          folder: "documents",
          extension,
          originalName: file.name,
          mimeType: file.type,
          size: file.size,
          scope,
          clientId: scope === "client" ? id : null,
          projectId: scope === "project" ? id : null,
          tags: [],
          name,
          description,
          folderPath: normalizedFolderPath,
          category: normalizedFolderPath,
          uploadedAt: new Date(),
        };

        // Upload original file
        const result = await uploadFile(file, metadata);
        await enforceDocumentMetadata({
          uploadResult: result,
          fallbackKey: `documents/${key}${extension || ""}`,
          name,
          description,
          folderPath: normalizedFolderPath,
        });
        uploaded.push({ ...result, metadata });

        // If PDF, also render and upload per-page images
        if (file.type === "application/pdf") {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 2.0 });

            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: context, viewport }).promise;

            const blob = await new Promise((resolve) =>
              canvas.toBlob(resolve, "image/png"),
            );

            const pageKey = `${uuidv4()}`;
            const pageMetadata = {
              key: pageKey,
              folder: "converted",
              extension: ".png",
              originalName: `page-${i}.png`,
              mimeType: "image/png",
              size: blob.size,
              scope,
              clientId: scope === "client" ? id : null,
              projectId: scope === "project" ? id : null,
              tags: [],
              name: `${name} - Page ${i}`,
              description,
              folderPath: normalizedFolderPath,
              category: normalizedFolderPath,
              uploadedAt: new Date(),
              pageNumber: i,
              parentKey: `documents/${key}`, // link back to original PDF
            };

            const pageResult = await uploadFile(blob, pageMetadata);
            await enforceDocumentMetadata({
              uploadResult: pageResult,
              fallbackKey: `documents/${pageKey}.png`,
              name: `${name} - Page ${i}`,
              description,
              folderPath: normalizedFolderPath,
            });
            uploaded.push({ ...pageResult, metadata: pageMetadata });
          }
        }
      } catch (err) {
        console.error("Upload failed for", file.name, err);
      }
    }

    setResults(uploaded);
    setFiles([]); // Clear files after successful upload
    setUploading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!uploading) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Upload Document
        <IconButton
          onClick={onClose}
          disabled={uploading}
          size="small"
          aria-label="close upload"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          fullWidth
          margin="normal"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          label="Folder"
          fullWidth
          margin="normal"
          value={folderPath}
          onChange={(e) => setFolderPath(e.target.value)}
          disabled={folderMode === "existing"}
        />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={folderMode}
          onChange={(_e, nextMode) => {
            if (!nextMode) return;
            setFolderMode(nextMode);
          }}
          sx={{ mt: 1, mb: 1 }}
        >
          <ToggleButton value="new">New Folder</ToggleButton>
          <ToggleButton
            value="existing"
            disabled={existingFolders.length === 0}
          >
            Existing Folder
          </ToggleButton>
        </ToggleButtonGroup>

        {folderMode === "existing" && (
          <FormControl
            fullWidth
            margin="normal"
            disabled={loadingFolders || existingFolders.length === 0}
          >
            <InputLabel id="existing-folder-label">Existing Folder</InputLabel>
            <Select
              labelId="existing-folder-label"
              label="Existing Folder"
              value={selectedExistingFolder}
              onChange={(e) => setSelectedExistingFolder(e.target.value)}
            >
              {existingFolders.map((path) => (
                <MenuItem
                  key={path}
                  value={path}
                >
                  {path}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography>Select one or more documents:</Typography>
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ marginTop: "1rem" }}
        />

        {uploading && <LinearProgress style={{ marginTop: "1rem" }} />}

        {results.length > 0 && (
          <div style={{ marginTop: "1rem" }}>
            <Typography variant="subtitle1">Uploaded Files:</Typography>
            <ul>
              {results.map((res) => (
                <li key={res.metadata.key}>
                  <a
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {res.metadata.name || res.metadata.key}
                  </a>
                  <Typography variant="body2">
                    {res.metadata.description}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={uploading}
        >
          Close
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={
            !files.length ||
            uploading ||
            (folderMode === "existing" && !effectiveFolderPath)
          }
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentModal;
